import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { BnLoadingService } from '@bndes/bndes-ux4/bndes-angular-utils';
import { BnAlertsService } from '@bndes/bndes-ux4/bn-alerts';
import { BnModalMessageService } from '@bndes/bndes-ux4/bn-modal-message';
import { BnUsuarioService } from '@bndes/bndes-ux4/bn-usuario';
import { ModalidadeService } from 'src/app/shared/services/modalidade.service';
import { RenunciaLimiteAdicionalService } from '../renuncia-limite-adicional.service';
import { LimiteAdicionalTranch } from 'src/app/model/limite-adicional-tranch.model';
import { LimiteAdicionalTrancheAgenteFinanceiro } from 'src/app/model/limite-adicional-tranche-agente-financeiro.model';

@Component({
  selector: 'app-renuncia-limite-adicional-solicitacao',
  templateUrl: './solicitacao.component.html',
  styleUrls: ['./solicitacao.component.scss'],
  standalone: false,
})
export class SolicitacaoComponent implements OnInit, OnDestroy {

  public solicitacaoForm: UntypedFormGroup;
  private destroy$ = new Subject<void>();

  public cnpjUsuario: string;
  public checkCiencia = false;
  public textoTermoRenunciaLimiteAdicional: string;
  public semValorDisponivel = false;

  public ideTranche: number;
  public ideAgente: number;
  public valorDisponivel = 0;
  public limiteAdicionalSelecionado: LimiteAdicionalTranch;
  public listaLimiteAdicional: LimiteAdicionalTrancheAgenteFinanceiro[] = [];

  @ViewChild('mensagemTermoRenuncia') mensagemTermoRenuncia: TemplateRef<any>;
  @ViewChild('botoesTermosRenuncia') botoesTermosRenuncia: TemplateRef<any>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private bnUsuarioService: BnUsuarioService,
    private bnLoadingService: BnLoadingService,
    private bnAlertsService: BnAlertsService,
    private modalidadeService: ModalidadeService,
    private modalService: BnModalMessageService,
    private service: RenunciaLimiteAdicionalService,
  ) {}

  ngOnInit(): void {
    this.construirFormulario();

    this.bnUsuarioService.usuario$
      .pipe(takeUntil(this.destroy$))
      .subscribe(usuario => {
        this.cnpjUsuario = usuario && usuario.cnpj ? usuario.cnpj : 'BNDES';
      });

    this.modalidadeService.modalidade$
      .pipe(takeUntil(this.destroy$))
      .subscribe(modalidadeSigla => {
        const modalidade = this.modalidadeService.getModalidades()
          .find(m => m.sigla === modalidadeSigla);

        this.textoTermoRenunciaLimiteAdicional =
          modalidade ? modalidade.textoTermoRenunciaLimiteAdicional : '';
      });

    this.ideTranche = Number(this.route.snapshot.paramMap.get('ideTranche')) || undefined;
    this.ideAgente = Number(this.route.snapshot.paramMap.get('ideAgente')) || undefined;

    this.buscarListaLimiteAdicionalPermitido();
  }

  private construirFormulario() {
    this.solicitacaoForm = this.formBuilder.group({
      limiteAdicional: [null, Validators.required],
      habilitacaoFaixaPj: [null, Validators.required],
      renunciaTotal: [null, Validators.required],
      tipoInformacao: [null],
      valorParcial: [null, this.validarValorParcial.bind(this)],
      percentualParcial: [null, this.validarPercentual.bind(this)],
    });

    this.solicitacaoForm.get('renunciaTotal').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(total => this.aplicarRegraRenunciaTotal(total));

    this.solicitacaoForm.get('tipoInformacao').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(tipo => this.aplicarRegraTipoInformacao(tipo));

    this.solicitacaoForm.get('limiteAdicional').valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(ideLimite => {
        if (ideLimite) {
          this.carregarValorDisponivel(ideLimite);
        }
      });
  }

  private aplicarRegraRenunciaTotal(renunciaTotal: boolean) {
    this.solicitacaoForm.get('tipoInformacao').reset();
    this.solicitacaoForm.get('valorParcial').reset();
    this.solicitacaoForm.get('percentualParcial').reset();
    this.revalidarParciais();
  }

  private aplicarRegraTipoInformacao(tipo: string) {
    if (tipo === 'VALOR') {
      this.solicitacaoForm.get('percentualParcial').reset();
    } else if (tipo === 'PERCENTUAL') {
      this.solicitacaoForm.get('valorParcial').reset();
    }
    this.revalidarParciais();
  }

  private revalidarParciais() {
    this.solicitacaoForm.get('valorParcial').updateValueAndValidity();
    this.solicitacaoForm.get('percentualParcial').updateValueAndValidity();
  }

  private preenchido(valor: any): boolean {
    return valor != null && valor !== '';
  }

  private validarValorParcial(control): ValidationErrors | null {
    if (!this.solicitacaoForm || !this.preenchido(control.value)) {
      return null;
    }

    const valor = Number(control.value);

    if (valor <= 0) {
      return { valorInvalido: true };
    }

    if (valor > this.valorDisponivel) {
      return { valorMaiorQueDisponivel: true };
    }

    return null;
  }

  private validarPercentual(control): ValidationErrors | null {
      if (!this.solicitacaoForm || !this.preenchido(control.value)) {
        return null;
      }

      const valorStr = String(control.value).replace(',', '.');
      const percentual = Number(valorStr);
      const decimais = valorStr.split('.')[1];

      if (
        isNaN(percentual) ||
        percentual <= 0 ||
        percentual > 100 ||
        (decimais && decimais.length > 2)
      ) {
        return { percentualInvalido: true };
      }

      return null;
  }

  private buscarListaLimiteAdicionalPermitido() {
    if (!this.ideAgente || !this.ideTranche) {
      return;
    }

    this.service.getLimiteAdicionalPermitido(this.ideAgente).subscribe(limites => {
      this.listaLimiteAdicional = limites;
    });
  }

  private carregarValorDisponivel(ideLimiteAdicionalPorTrancheAgenteFinanceiro: number) {
    if (!this.ideAgente || !this.ideTranche || !ideLimiteAdicionalPorTrancheAgenteFinanceiro) {
      return;
    }

    this.service.getValorDisponivel(ideLimiteAdicionalPorTrancheAgenteFinanceiro).subscribe(valor => {
      this.valorDisponivel = valor || 0;
      this.semValorDisponivel = this.valorDisponivel <= 0;

      if (this.semValorDisponivel) {
        this.bnAlertsService.alertaErro(
          'Atenção',
          'Não há valor disponível para renúncia do Limite Adicional para este agente financeiro.'
        );
      }
      
      this.solicitacaoForm.get('valorParcial').updateValueAndValidity();
    });
  }

  public podeSalvar(): boolean {
    const limiteAdicional = this.solicitacaoForm.get('limiteAdicional');

    if (!limiteAdicional.valid) {
      return false;
    }

    if (this.semValorDisponivel) {
      return false;
    }
    const habilitacao = this.solicitacaoForm.get('habilitacaoFaixaPj');
    const total = this.solicitacaoForm.get('renunciaTotal');

    if (!habilitacao.valid || !total.valid) {
      return false;
    }

    if (total.value === true) {
      return true;
    }

    const tipo = this.solicitacaoForm.get('tipoInformacao').value;
    const valor = this.solicitacaoForm.get('valorParcial');
    const percentual = this.solicitacaoForm.get('percentualParcial');

    if (tipo === 'VALOR') {
      return this.preenchido(valor.value)
        && valor.valid
        && !this.preenchido(percentual.value);
    }

    if (tipo === 'PERCENTUAL') {
      return this.preenchido(percentual.value)
        && percentual.valid
        && !this.preenchido(valor.value);
    }

    return false;
  }

  public cancelar() {
    this.router.navigate(['renuncia-limite-adicional', 'pesquisa']);
  }

  public abrirModalConfirmar() {
    if (!this.podeSalvar()) {
      this.solicitacaoForm.markAllAsTouched();
      return;
    }

    this.modalService.abrir(
      'Termos e condições',
      null,
      'md',
      this.mensagemTermoRenuncia,
      this.botoesTermosRenuncia
    );
  }

  public desistir() {
    this.checkCiencia = false;
    this.modalService.dismiss('abortou solicitação de renúncia');
  }

  public async salvar() {
    if (!this.checkCiencia) {
      return;
    }

    try {
      this.bnLoadingService.bloqueiaTela();

      const dados = {
        ideLimiteAdicionalPorTrancheAgenteFinanceiro: this.solicitacaoForm.get('limiteAdicional').value,
        ideTranche: this.ideTranche,
        ideAgente: this.ideAgente,
        cnpjUsuario: this.cnpjUsuario,
        habilitacaoFaixaPj: this.solicitacaoForm.get('habilitacaoFaixaPj').value,
        renunciaTotal: this.solicitacaoForm.get('renunciaTotal').value,
        valorParcial: this.solicitacaoForm.get('valorParcial').value,
        percentualParcial: this.solicitacaoForm.get('percentualParcial').value,
      };

      await this.service.solicitar(dados).toPromise();
      this.modalService.dismiss('confirmado');

      this.router.navigate(['renuncia-limite-adicional', 'pesquisa']);

      this.bnAlertsService.alertaSucesso(
        'Sucesso',
        'Solicitação de renúncia enviada e processada pelo sistema.'
      );
    } catch (err) {
      if (!err.Error) {
        this.bnAlertsService.alertaErro(
          'Erro',
          'Erro ao solicitar renúncia'
        );
      }
    } finally {
      this.checkCiencia = false;
      this.bnLoadingService.liberaTela();
    }
  }

  public temErro(campo: string, erro: string): boolean {
    return this.solicitacaoForm.get(campo).hasError(erro);
  }

  public limitarPercentual(event: any) {
    let valor: string = (event.target.value || '')
      .replace(/[^0-9.,]/g, '')
      .replace(',', '.');

    const idx = valor.indexOf('.');

    if (idx >= 0) {
      valor =
        valor.substring(0, idx + 1) +
        valor
          .substring(idx + 1)
          .replace(/\./g, '')
          .slice(0, 2);
    }

    this.solicitacaoForm.get('percentualParcial').setValue(valor);
  }

  formatarMoeda(moeda) {
    if (!isNaN(moeda)) {
        return Number(moeda).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    }
    return '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}